import React, { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Dialog,
  Stack,
  Typography,
  useTheme,
  Snackbar,
} from "@mui/material";
import {
  GridInitialState,
  GridRowSelectionModel,
  GridColDef,
} from "@mui/x-data-grid-premium";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { format, set, startOfToday } from "date-fns";

import { formatDateYYYYMMDD } from "utils/formatting";
import GaneshDataGrid from "../../components/DataGrid/GaneshDataGrid";
import TableCellBooleanIcon from "../../components/UI/TableCellBooleanIcon";
import useGaneshApi from "../../hooks/useGaneshApi";
import {
  RiPencilLine,
  RiInformationLine,
  RiCheckboxCircleLine,
  RiDeleteBinLine,
  RiAddCircleLine,
} from "@remixicon/react";
import DeliveriesDialog from "../../components/Schedule/Dialogs/DeliveriesDialog";
import DeliveriesRemoveDialog from "../../components/Schedule/Dialogs/DeliveriesRemoveDialog";
import { useDeliveryDialog } from "../../hooks/useDeliveryDialog";
import { Delivery_T } from "../../types/Delivery";
import {
  DialogTypeT,
  DialogActionMessagesT,
  DialogTypesT,
} from "../../types/ScheduleForm";

// todo: standardize this
const defaultGridState: GridInitialState = {
  filter: {
    filterModel: {
      items: [],
    },
  },
  sorting: { sortModel: [{ field: "delivery_id", sort: "asc" }] },
  pagination: { paginationModel: { pageSize: 50, page: 0 } },
  pinnedColumns: { left: [] },
  columns: { columnVisibilityModel: {} },
  density: "compact",
};

export default function ScheduleByDatePage() {
  const { apiRequest } = useGaneshApi();
  const { date_id } = useParams();
  const queryClient = useQueryClient();
  const theme = useTheme();

  const summaryLinkDate = date_id ? format(new Date(date_id), "yyyy-MM") : "";
  const { openDialogType, setOpenDialogType } = useDeliveryDialog() as {
    openDialogType: DialogTypeT;
    setOpenDialogType: (type: DialogTypeT) => void;
  };

  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<
    "success" | "error" | "warning"
  >("success");

  const parsedDate = date_id ? formatDateYYYYMMDD(new Date(date_id)) : null;
  const isDatePast = parsedDate
    ? parsedDate < formatDateYYYYMMDD(startOfToday())
    : false;

  const { data, isLoading, error } = useQuery<any, Error>({
    queryKey: ["fulfillments_schedules", date_id],
    queryFn: async () => {
      const response = await apiRequest<Delivery_T[]>(
        `/v1/fulfillments_schedules?dateKey=${date_id}`,
        {
          method: "GET",
        }
      );
      return response;
    },
    retry: 5,
  });

  useEffect(() => {
    document.title = `Schedule Details: ${date_id}`;
  }, [date_id]);

  const columns: GridColDef[] = [
    { field: "delivery_fulfillment_id", headerName: "ID", width: 100 },
    { field: "delivery_name", headerName: "Delivery Name", width: 200 },
    {
      field: "volume_upgrade_scheduled",
      headerName: "VU Scheduled",
      width: 130,
      renderCell: ({ row }: { row: { volume_upgrade_scheduled: boolean } }) => (
        <TableCellBooleanIcon value={row.volume_upgrade_scheduled} />
      ),
    },
    { field: "customer_name", headerName: "Customer", width: 200 },
    {
      field: "fulfillment_group_name",
      headerName: "Fulfillment Groups",
      width: 200,
    },
    {
      field: "countries",
      headerName: "Countries",
      width: 200,
      valueGetter: (countries: []) => {
        if (countries && countries.length > 0) {
          return countries.map(({ name }) => name).join(", ");
        }
        return "";
      },
    },
    {
      field: "fulfillment_destination",
      headerName: "Fulfillment Dest.",
      width: 200,
    },
  ];

  const handleRowSelectionModelChange = useCallback(
    (newRowSelectionModel: GridRowSelectionModel) => {
      setRowSelectionModel(newRowSelectionModel);
    },
    []
  );

  const handleCloseChangeDialog = () => {
    setOpenDialogType(DialogTypesT.NONE);
  };

  const handleSuccessCallback = (responseData?: any) => {
    queryClient.invalidateQueries({
      queryKey: ["fulfillments_schedules", date_id],
    });

    const action = openDialogType;
    let message = "";
    let severity: "success" | "error" | "warning" = "success";

    // Check if we have response data to determine the actual status
    if (responseData) {
      const responseStatus =
        responseData?.status || responseData?.response?.status;
      const isPartialSuccess = responseStatus === 207;

      if (
        isPartialSuccess &&
        responseData?.response?.data?.errors?.length > 0
      ) {
        // Partial success - some succeeded, some failed
        const successCount = responseData.response.data.details?.length || 0;
        const errorCount = responseData.response.data.errors?.length || 0;
        message = `${successCount} deliveries ${
          action === DialogTypesT.ADD
            ? "scheduled"
            : action === DialogTypesT.CHANGE
            ? "updated"
            : "removed"
        } successfully. ${errorCount} failed.`;
        severity = "warning";
      } else {
        // Complete success
        message =
          action === DialogTypesT.ADD
            ? DialogActionMessagesT.ADD
            : action === DialogTypesT.CHANGE || action === DialogTypesT.REMOVE
            ? DialogActionMessagesT[action](rowSelectionModel.length)
            : "Success!";
        severity = "success";
      }
    } else {
      // Fallback to default message
      message =
        action === DialogTypesT.ADD
          ? DialogActionMessagesT.ADD
          : action === DialogTypesT.CHANGE || action === DialogTypesT.REMOVE
          ? DialogActionMessagesT[action](rowSelectionModel.length)
          : "Success!";
      severity = "success";
    }

    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
    setRowSelectionModel([]);
    setOpenDialogType(DialogTypesT.NONE);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleErrorCallback = (error: any) => {
    console.error("Delivery operation failed:", error);

    // Extract error message from the response
    let errorMessage = "Failed to schedule deliveries. Please try again.";

    if (error?.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error?.message) {
      errorMessage = error.message;
    } else if (typeof error === "string") {
      errorMessage = error;
    }

    setSnackbarSeverity("error");
    setSnackbarMessage(errorMessage);
    setSnackbarOpen(true);
    setOpenDialogType(DialogTypesT.NONE);
  };

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Deliveries Schedule Management
      </Typography>
      <Stack spacing={3}>
        <Box>
          <Breadcrumbs aria-label="breadcrumb">
            <Link color="inherit" to={`/schedule/summary/${summaryLinkDate}`}>
              Schedule
            </Link>
            <Typography color="textPrimary">{`${date_id} Details`}</Typography>
          </Breadcrumbs>
        </Box>
        {error && <Alert severity="error">{error.message}</Alert>}
        {!error && (
          <Box sx={{ mb: 2, display: "flex", flexDirection: "column", gap: 1 }}>
            {isDatePast ? (
              <Alert
                severity="info"
                icon={
                  <RiInformationLine
                    size={20}
                    style={{ color: theme.palette.info.dark, marginTop: 1 }}
                  />
                }
              >
                These deliveries are in the past and cannot be modified.
              </Alert>
            ) : (
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={rowSelectionModel.length === 0}
                    onClick={() => setOpenDialogType(DialogTypesT.CHANGE)}
                  >
                    <RiPencilLine size={16} style={{ marginRight: 4 }} /> Change
                  </Button>
                  <Button
                    variant="contained"
                    color="error"
                    disabled={rowSelectionModel.length === 0}
                    onClick={() => setOpenDialogType(DialogTypesT.REMOVE)}
                  >
                    <RiDeleteBinLine size={16} style={{ marginRight: 4 }} />{" "}
                    Remove
                  </Button>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setOpenDialogType(DialogTypesT.ADD)}
                  >
                    <RiAddCircleLine size={16} style={{ marginRight: 4 }} /> Add
                  </Button>
                </Box>
                {openDialogType === DialogTypesT.ADD && (
                  <Dialog
                    open={true}
                    onClose={handleCloseChangeDialog}
                    maxWidth="md"
                    fullWidth
                  >
                    <DeliveriesDialog
                      onClose={handleCloseChangeDialog}
                      onSuccessCallback={handleSuccessCallback}
                      onErrorCallback={handleErrorCallback}
                      selectedRows={data?.data?.filter((row: any) =>
                        rowSelectionModel.includes(row.id)
                      )}
                    />
                  </Dialog>
                )}
                {openDialogType === DialogTypesT.CHANGE && (
                  <Dialog
                    open={true}
                    onClose={handleCloseChangeDialog}
                    maxWidth="md"
                    fullWidth
                  >
                    <DeliveriesDialog
                      onClose={handleCloseChangeDialog}
                      selectedRows={data?.data?.filter((row: any) =>
                        rowSelectionModel.includes(row.id)
                      )}
                      action="CHANGE"
                      onSuccessCallback={handleSuccessCallback}
                      onErrorCallback={handleErrorCallback}
                    />
                  </Dialog>
                )}
                {openDialogType === DialogTypesT.REMOVE && (
                  <Dialog
                    open={true}
                    onClose={handleCloseChangeDialog}
                    maxWidth="md"
                    fullWidth
                  >
                    <DeliveriesRemoveDialog
                      onClose={handleCloseChangeDialog}
                      selectedRows={data?.data?.filter((row: any) =>
                        rowSelectionModel.includes(row.id)
                      )}
                      onSuccessCallback={handleSuccessCallback}
                    />
                  </Dialog>
                )}
              </Box>
            )}
            <GaneshDataGrid
              defaultGridState={defaultGridState}
              gridId="schedule-by-date-details"
              loading={isLoading}
              data={data?.data || []}
              columns={columns}
              checkboxSelection={!isDatePast}
              disableRowSelectionOnClick
              rowSelectionModel={rowSelectionModel}
              onRowSelectionModelChange={handleRowSelectionModelChange}
              keepNonExistentRowsSelected
            />
            <Snackbar
              open={snackbarOpen}
              autoHideDuration={snackbarSeverity === "error" ? 6000 : 4000}
              onClose={handleSnackbarClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            >
              <Alert
                onClose={handleSnackbarClose}
                severity={snackbarSeverity}
                sx={{ width: "100%" }}
                icon={
                  snackbarSeverity === "success" ? (
                    <RiCheckboxCircleLine size={24} />
                  ) : undefined
                }
              >
                <Box>
                  <Typography variant="body1">{snackbarMessage}</Typography>
                </Box>
              </Alert>
            </Snackbar>
          </Box>
        )}
      </Stack>
    </Box>
  );
}
